import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { partnerApi, Partner, PartnerCommission, PartnerWithdrawal, PartnerWithdrawalStats, PartnerDashboard } from '../api/client';

interface PartnerContextType {
  partner: Partner | null;
  partnerDashboard: PartnerDashboard | null;
  commissions: PartnerCommission[];
  withdrawals: PartnerWithdrawal[];
  withdrawalStats: PartnerWithdrawalStats | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isPartnerApproved: boolean;
  showLogoutModal: boolean;
  showPartnerModal: boolean;
  logout: () => void;
  showLogoutConfirmation: () => void;
  hideLogoutConfirmation: () => void;
  showPartnerModalConfirmation: () => void;
  hidePartnerModalConfirmation: () => void;
  refreshProfile: () => Promise<void>;
  refreshPartnerDashboard: () => Promise<void>;
  refreshCommissions: () => Promise<void>;
  refreshWithdrawals: () => Promise<void>;
  refreshWithdrawalStats: () => Promise<void>;
  createWithdrawal: (usdtWallet: string, amount: number) => Promise<PartnerWithdrawal>;
  cancelWithdrawal: (withdrawalId: string) => Promise<void>;
  forceCheckAuth: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);

export const usePartner = () => {
  const context = useContext(PartnerContext);
  if (context === undefined) {
    throw new Error('usePartner must be used within a PartnerProvider');
  }
  return context;
};

interface PartnerProviderProps {
  children: ReactNode;
}

export const PartnerProvider: React.FC<PartnerProviderProps> = ({ children }) => {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [partnerDashboard, setPartnerDashboard] = useState<PartnerDashboard | null>(null);
  const [commissions, setCommissions] = useState<PartnerCommission[]>([]);
  const [withdrawals, setWithdrawals] = useState<PartnerWithdrawal[]>([]);
  const [withdrawalStats, setWithdrawalStats] = useState<PartnerWithdrawalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPartnerApproved, setIsPartnerApproved] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);

  // Проверяем авторизацию при загрузке (guard от двойного вызова StrictMode)
  const hasInitRef = useRef(false);
  const authInProgressRef = useRef(false);

  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    checkAuth();
  }, []);

  const checkAuth = async () => {
    if (authInProgressRef.current) return;
    authInProgressRef.current = true;
    try {
      setLoading(true);
      const token = localStorage.getItem('partner_token');

      if (token) {
        try {
          const p = await partnerApi.getPartnerProfile();
          setPartner(p);
          setIsAuthenticated(true);
          setIsPartnerApproved(true);
          setError(null);
          await Promise.all([
            loadPartnerDashboard(),
            loadCommissions(),
            loadWithdrawals(),
            loadWithdrawalStats(),
          ]);
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 403) {
            setIsAuthenticated(true);
            setIsPartnerApproved(false);
            setPartner(null);
          } else {
            localStorage.removeItem('partner_token');
            setIsAuthenticated(false);
            setIsPartnerApproved(false);
            setPartner(null);
          }
        }
      } else {
        setIsAuthenticated(false);
        setIsPartnerApproved(false);
      }
    } catch (err) {
      setIsAuthenticated(false);
      localStorage.removeItem('partner_token');
    } finally {
      setLoading(false);
      authInProgressRef.current = false;
    }
  };

  const loadProfile = async () => {
    try {
      const partner = await partnerApi.getPartnerProfile();
      setPartner(partner);
      setIsPartnerApproved(true);
      setError(null);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setIsPartnerApproved(false);
        setPartner(null);
      } else if (status === 401) {
        localStorage.removeItem('partner_token');
        setIsAuthenticated(false);
        setIsPartnerApproved(false);
        setPartner(null);
      }
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message || 'Ошибка загрузки профиля');
    }
  };

  const loadPartnerDashboard = async () => {
    try {
      const dashboard = await partnerApi.getPartnerDashboard();
      setPartnerDashboard(dashboard);
    } catch (err: any) {
      // Failed to load partner dashboard
    }
  };

  const loadCommissions = async () => {
    try {
      const response = await partnerApi.getPartnerCommissions();
      setCommissions(response.commissions);
    } catch (err: any) {
      // Failed to load commissions
    }
  };

  const loadWithdrawals = async () => {
    try {
      const response = await partnerApi.getPartnerWithdrawals();
      setWithdrawals(response.items);
    } catch (err: any) {
      // Failed to load withdrawals
    }
  };

  const loadWithdrawalStats = async () => {
    try {
      const stats = await partnerApi.getPartnerWithdrawalStats();
      setWithdrawalStats(stats);
    } catch (err: any) {
      // Failed to load withdrawal stats
    }
  };

  const logout = () => {
    localStorage.removeItem('partner_token');
    setPartner(null);
    setPartnerDashboard(null);
    setCommissions([]);
    setIsAuthenticated(false);
    setIsPartnerApproved(false);
    setError(null);
  };

  const refreshProfile = async () => {
    if (isAuthenticated) {
      await loadProfile();
    }
  };



  const refreshPartnerDashboard = async () => {
    if (isAuthenticated) {
      await loadPartnerDashboard();
    }
  };

  const refreshCommissions = async () => {
    if (isAuthenticated) {
      await loadCommissions();
    }
  };

  const refreshWithdrawals = async () => {
    await loadWithdrawals();
  };

  const refreshWithdrawalStats = async () => {
    await loadWithdrawalStats();
  };

  const createWithdrawal = async (usdtWallet: string, amount: number): Promise<PartnerWithdrawal> => {
    try {
      const withdrawal = await partnerApi.createPartnerWithdrawal({ usdtWallet, amount });
      await loadWithdrawals();
      await loadWithdrawalStats();
      return withdrawal;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Ошибка создания заявки на выплату');
    }
  };

  const cancelWithdrawal = async (withdrawalId: string): Promise<void> => {
    try {
      await partnerApi.cancelPartnerWithdrawal(withdrawalId);
      await loadWithdrawals();
      await loadWithdrawalStats();
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Ошибка отмены заявки на выплату');
    }
  };

  const forceCheckAuth = useCallback(async () => {
    await checkAuth();
  }, []);

  const showLogoutConfirmation = () => {
    setShowLogoutModal(true);
  };

  const hideLogoutConfirmation = () => {
    setShowLogoutModal(false);
  };

  const showPartnerModalConfirmation = () => {
    setShowPartnerModal(true);
  };

  const hidePartnerModalConfirmation = () => {
    setShowPartnerModal(false);
  };

  const value: PartnerContextType = {
    partner,
    partnerDashboard,
    commissions,
    withdrawals,
    withdrawalStats,
    loading,
    error,
    isAuthenticated,
    isPartnerApproved,
    showLogoutModal,
    showPartnerModal,
    logout,
    showLogoutConfirmation,
    hideLogoutConfirmation,
    showPartnerModalConfirmation,
    hidePartnerModalConfirmation,
    refreshProfile,
    refreshPartnerDashboard,

    refreshCommissions,
    refreshWithdrawals,
    refreshWithdrawalStats,
    createWithdrawal,
    cancelWithdrawal,
    forceCheckAuth,
  };

  return (
    <PartnerContext.Provider value={value}>
      {children}
    </PartnerContext.Provider>
  );
};

export default PartnerContext;